package com.evoapps.evoshift.ui.announcements

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.ui.theme.animateItemIfMotion
import com.evoapps.evoshift.domain.util.AppClock
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Campaign
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.DoneAll
import androidx.compose.material.icons.filled.PushPin
import androidx.compose.material.icons.outlined.PushPin
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Announcement
import com.evoapps.evoshift.domain.model.ReadReceipts
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.CentredLazyColumn
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndDate
import com.evoapps.evoshift.viewmodels.AnnouncementsViewModel
import com.evoapps.evoshift.viewmodels.Gate
import com.evoapps.evoshift.viewmodels.ReceiptsView
import com.evoapps.evoshift.viewmodels.shows
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoConfirmDialog
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoSwitchRow
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.announcement_audience
import evoshift.composeapp.generated.resources.announcement_compose_hint
import evoshift.composeapp.generated.resources.announcement_delete_text
import evoshift.composeapp.generated.resources.announcement_delete_title
import evoshift.composeapp.generated.resources.announcement_everyone
import evoshift.composeapp.generated.resources.announcement_for_role
import evoshift.composeapp.generated.resources.announcement_former_author
import evoshift.composeapp.generated.resources.announcement_new
import evoshift.composeapp.generated.resources.announcement_pin
import evoshift.composeapp.generated.resources.announcement_pinned
import evoshift.composeapp.generated.resources.announcement_post
import evoshift.composeapp.generated.resources.announcement_read_by
import evoshift.composeapp.generated.resources.announcement_receipts_none
import evoshift.composeapp.generated.resources.announcement_receipts_not_yet
import evoshift.composeapp.generated.resources.announcement_receipts_privacy
import evoshift.composeapp.generated.resources.announcement_receipts_read
import evoshift.composeapp.generated.resources.announcement_receipts_title
import evoshift.composeapp.generated.resources.announcement_text
import evoshift.composeapp.generated.resources.announcement_too_long
import evoshift.composeapp.generated.resources.announcement_unpin
import evoshift.composeapp.generated.resources.announcement_unread
import evoshift.composeapp.generated.resources.announcements_empty
import evoshift.composeapp.generated.resources.announcements_empty_manager
import evoshift.composeapp.generated.resources.announcements_empty_text
import evoshift.composeapp.generated.resources.announcements_title
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.delete
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Instant

/** Test tags of the screen (robots find posts by their text, these are for the buttons). */
object AnnouncementTags {
    const val NEW = "announcement_new"
    const val TEXT = "announcement_text"
    fun receipts(id: String) = "announcement_receipts_$id"
}

/**
 * Team announcements (SH-118): the managers' posts for this person, pinned first, then the newest;
 * the ones not read before are marked "new" (opening the screen reads them). Managers post (to
 * everyone or one role, pinned or not), pin, delete and see who read a post. No chat: employees
 * only read.
 */
@Composable
fun AnnouncementsScreen(viewModel: AnnouncementsViewModel, onNavigateBack: () -> Unit) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val shown = (state.error ?: state.message)?.asString()
    LaunchedEffect(shown) {
        shown?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    val canPost = state.capabilities.shows(Gate.ANNOUNCEMENTS_POSTING)
    val seesReceipts = state.capabilities.shows(Gate.READ_RECEIPTS)

    Scaffold(
        containerColor = Cream,
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.announcements_title), onBack = onNavigateBack, showWorkplace = true) },
        floatingActionButton = {
            if (canPost) {
                ExtendedFloatingActionButton(
                    onClick = viewModel::openComposer,
                    containerColor = DarkGold,
                    contentColor = DeepNavy,
                    icon = { Icon(Icons.Default.Add, contentDescription = null) },
                    text = { Text(stringResource(Res.string.announcement_new)) },
                    modifier = Modifier.testTag(AnnouncementTags.NEW)
                )
            }
        }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize()) {
            when {
                state.isLoading -> CircularProgressIndicator(Modifier.align(Alignment.Center), color = DarkGold)
                state.announcements.isEmpty() -> EmptyFeed(canPost, Modifier.align(Alignment.Center))
                else -> CentredLazyColumn(
                    maxWidth = FormMaxWidth,
                    contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 16.dp, bottom = 96.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(state.announcements, key = { it.id }) { post ->
                        Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                            AnnouncementCard(
                                post = post,
                                unread = post.id in state.unread,
                                canEdit = canPost,
                                seesReceipts = seesReceipts,
                                onPin = { viewModel.setPinned(post, !post.pinned) },
                                onDelete = { viewModel.askDelete(post) },
                                onReceipts = { viewModel.openReceipts(post) }
                            )
                        }
                    }
                }
            }
        }
    }

    if (state.composerOpen) {
        ComposerDialog(
            roles = state.roles,
            isPosting = state.isPosting,
            onPost = viewModel::post,
            onDismiss = viewModel::closeComposer
        )
    }
    state.receipts?.let { ReceiptsDialog(it, onDismiss = viewModel::closeReceipts) }
    state.confirmDelete?.let { post ->
        EvoConfirmDialog(
            title = stringResource(Res.string.announcement_delete_title),
            text = stringResource(Res.string.announcement_delete_text),
            confirmLabel = stringResource(Res.string.delete),
            onConfirm = { viewModel.delete(post) },
            onDismiss = { viewModel.askDelete(null) },
            destructive = true,
            icon = Icons.Default.Delete
        )
    }
}

@Composable
private fun EmptyFeed(canPost: Boolean, modifier: Modifier) {
    Column(modifier.padding(32.dp), horizontalAlignment = Alignment.CenterHorizontally) {
        Icon(Icons.Default.Campaign, contentDescription = null, tint = DarkGold, modifier = Modifier.size(48.dp))
        Spacer(Modifier.size(12.dp))
        Text(stringResource(Res.string.announcements_empty), style = MaterialTheme.typography.titleMedium, color = DeepNavy, fontWeight = FontWeight.Bold)
        Text(
            stringResource(if (canPost) Res.string.announcements_empty_manager else Res.string.announcements_empty_text),
            style = MaterialTheme.typography.bodyMedium,
            color = DeepNavy.copy(alpha = 0.7f),
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(top = 4.dp)
        )
    }
}

@Composable
private fun AnnouncementCard(
    post: Announcement,
    unread: Boolean,
    canEdit: Boolean,
    seesReceipts: Boolean,
    onPin: () -> Unit,
    onDelete: () -> Unit,
    onReceipts: () -> Unit
) {
    RegularCard(modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.weight(1f)) {
                    Text(
                        post.authorName ?: stringResource(Res.string.announcement_former_author),
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        color = DeepNavy
                    )
                    Text(postedAt(post.createdAt), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.6f))
                }
                if (unread) Chip(stringResource(Res.string.announcement_unread), DarkGold, DeepNavy)
                if (canEdit) {
                    IconButtonWithTooltip(onClick = onPin, tooltip = stringResource(if (post.pinned) Res.string.announcement_unpin else Res.string.announcement_pin)) {
                        Icon(
                            if (post.pinned) Icons.Default.PushPin else Icons.Outlined.PushPin,
                            contentDescription = stringResource(if (post.pinned) Res.string.announcement_unpin else Res.string.announcement_pin),
                            tint = if (post.pinned) DarkGold else DeepNavy.copy(alpha = 0.5f)
                        )
                    }
                    IconButtonWithTooltip(onClick = onDelete, tooltip = stringResource(Res.string.delete)) {
                        Icon(Icons.Default.Delete, contentDescription = stringResource(Res.string.delete), tint = DeepNavy.copy(alpha = 0.5f))
                    }
                }
            }
            if (post.pinned || post.targetRole != null) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (post.pinned) Chip(stringResource(Res.string.announcement_pinned), DeepNavy.copy(alpha = 0.08f), DeepNavy, Icons.Default.PushPin)
                    post.targetRole?.let { Chip(stringResource(Res.string.announcement_for_role, it), DeepNavy.copy(alpha = 0.08f), DeepNavy) }
                }
            }
            Text(post.text, style = MaterialTheme.typography.bodyLarge, color = DeepNavy)
            if (seesReceipts) {
                TextButton(onClick = onReceipts, modifier = Modifier.testTag(AnnouncementTags.receipts(post.id))) {
                    Icon(Icons.Default.DoneAll, contentDescription = null, tint = DarkGold, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(6.dp))
                    Text(stringResource(Res.string.announcement_receipts_title), color = DeepNavy)
                }
            }
        }
    }
}

@Composable
private fun Chip(text: String, background: Color, content: Color, icon: androidx.compose.ui.graphics.vector.ImageVector? = null) {
    Row(
        Modifier.clip(RoundedCornerShape(50)).background(background).padding(horizontal = 10.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        icon?.let {
            Icon(it, contentDescription = null, tint = content, modifier = Modifier.size(14.dp))
            Spacer(Modifier.width(4.dp))
        }
        Text(text, style = MaterialTheme.typography.labelMedium, color = content, fontWeight = FontWeight.SemiBold)
    }
}

/** "14:05" today, else "Tuesday, 3 February, 14:05", in the device's time zone. */
@Composable
private fun postedAt(createdAt: String): String {
    val zone = TimeZone.currentSystemDefault()
    val at = remember(createdAt) { runCatching { Instant.parse(createdAt).toLocalDateTime(zone) }.getOrNull() } ?: return ""
    val today = remember { AppClock.now().toLocalDateTime(zone).date }
    val time = "${at.hour.toString().padStart(2, '0')}:${at.minute.toString().padStart(2, '0')}"
    return if (at.date == today) time else "${dayAndDate(at.date)}, $time"
}

@Composable
private fun ComposerDialog(
    roles: List<String>,
    isPosting: Boolean,
    onPost: (String, Boolean, String?) -> Unit,
    onDismiss: () -> Unit
) {
    var text by rememberSaveable { mutableStateOf("") }
    var pinned by rememberSaveable { mutableStateOf(false) }
    // "" stands for everyone.
    var audience by rememberSaveable { mutableStateOf("") }
    val tooLong = text.trim().length > Announcement.MAX_TEXT
    val everyone = stringResource(Res.string.announcement_everyone)

    EvoDialog(
        title = stringResource(Res.string.announcement_new),
        icon = Icons.Default.Campaign,
        onDismiss = onDismiss,
        dismissible = !isPosting,
        dismissOnClickOutside = false,
        confirm = DialogAction(
            stringResource(Res.string.announcement_post),
            { onPost(text, pinned, audience.ifBlank { null }) },
            enabled = text.isNotBlank() && !tooLong,
            loading = isPosting
        )
    ) {
        EvoTextField(
            value = text,
            onValueChange = { text = it },
            label = stringResource(Res.string.announcement_text),
            singleLine = false,
            minLines = 4,
            supportingText = "${text.trim().length}/${Announcement.MAX_TEXT}",
            error = stringResource(Res.string.announcement_too_long, Announcement.MAX_TEXT).takeIf { tooLong },
            modifier = Modifier.testTag(AnnouncementTags.TEXT)
        )
        if (roles.isNotEmpty()) {
            EvoDropdown(
                label = stringResource(Res.string.announcement_audience),
                options = listOf("") + roles,
                selected = audience,
                onSelected = { audience = it },
                optionLabel = { if (it.isBlank()) everyone else it }
            )
        }
        EvoSwitchRow(checked = pinned, onCheckedChange = { pinned = it }, label = stringResource(Res.string.announcement_pin))
        Text(
            stringResource(Res.string.announcement_compose_hint),
            style = MaterialTheme.typography.bodySmall,
            color = Color.White.copy(alpha = 0.7f)
        )
    }
}

@Composable
private fun ReceiptsDialog(view: ReceiptsView, onDismiss: () -> Unit) {
    val receipts: ReadReceipts? = view.receipts
    EvoDialog(
        title = stringResource(Res.string.announcement_receipts_title),
        subtitle = receipts?.let { stringResource(Res.string.announcement_read_by, it.read.size, it.total) },
        icon = Icons.Default.DoneAll,
        onDismiss = onDismiss,
        confirm = null,
        dismiss = DialogAction(stringResource(Res.string.close), onDismiss)
    ) {
        if (receipts == null) {
            Box(Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = LightGold)
            }
            return@EvoDialog
        }
        DialogSection(stringResource(Res.string.announcement_receipts_read)) {
            if (receipts.read.isEmpty()) {
                Text(stringResource(Res.string.announcement_receipts_none), color = Color.White.copy(alpha = 0.7f))
            }
            receipts.read.forEachIndexed { index, (person, at) ->
                if (index > 0) HorizontalDivider(color = Color.White.copy(alpha = 0.08f))
                Row(Modifier.fillMaxWidth().padding(vertical = 6.dp), verticalAlignment = Alignment.CenterVertically) {
                    Text(person.displayName, color = Color.White, modifier = Modifier.weight(1f))
                    Text(postedAt(at), color = Color.White.copy(alpha = 0.6f), style = MaterialTheme.typography.bodySmall)
                }
            }
        }
        if (receipts.notYet.isNotEmpty()) {
            DialogSection(stringResource(Res.string.announcement_receipts_not_yet)) {
                receipts.notYet.forEachIndexed { index, person ->
                    if (index > 0) HorizontalDivider(color = Color.White.copy(alpha = 0.08f))
                    Text(person.displayName, color = Color.White.copy(alpha = 0.8f), modifier = Modifier.padding(vertical = 6.dp))
                }
            }
        }
        Text(
            stringResource(Res.string.announcement_receipts_privacy),
            style = MaterialTheme.typography.bodySmall,
            color = Color.White.copy(alpha = 0.6f)
        )
    }
}
